import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Loader2Icon,
  CheckCircle2Icon,
  XCircleIcon,
  InfinityIcon,
  TrendingUpIcon,
  WalletIcon,
  LinkIcon,
  BarChart3Icon,
  ZapIcon,
  LockIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAffiliateApplication } from "../hooks/use-affiliate-data";

// Brand colors
const GREEN = "#2ebd85";
const GREEN_LIGHT = "#5fd4a4";

// Animated background with glow orbs
const AnimatedBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Base gradient */}
    <div className="absolute inset-0 affiliate-hero-gradient" />
    
    {/* Dot pattern overlay */}
    <div className="absolute inset-0 bg-dot-pattern opacity-60" />
    
    {/* Animated glow orbs */}
    <div 
      className="glow-orb w-[500px] h-[500px] -top-32 -left-32"
      style={{ 
        background: `radial-gradient(circle, ${GREEN}30 0%, transparent 70%)`,
        animationDelay: "0s",
      }}
    />
    <div 
      className="glow-orb w-[400px] h-[400px] top-1/3 -right-20"
      style={{ 
        background: `radial-gradient(circle, ${GREEN}20 0%, transparent 70%)`,
        animationDelay: "-5s",
      }}
    />
    <div 
      className="glow-orb w-[300px] h-[300px] bottom-20 left-1/4"
      style={{ 
        background: `radial-gradient(circle, ${GREEN}15 0%, transparent 70%)`,
        animationDelay: "-10s",
      }}
    />
    
    {/* Center accent glow */}
    <div
      className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full opacity-20 blur-[120px]"
      style={{ background: GREEN }}
    />
  </div>
);

// Hero Section Component
const HeroSection = () => (
  <section className="relative py-16 md:py-24 px-4 overflow-hidden">
    <AnimatedBackground />
    
    <div className="relative z-10 max-w-4xl mx-auto text-center">
      {/* Badge */}
      <div 
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
        style={{ 
          background: `linear-gradient(135deg, ${GREEN}15 0%, transparent 100%)`,
          border: `1px solid ${GREEN}30`,
        }}
      >
        <InfinityIcon className="size-4" style={{ color: GREEN }} />
        <span className="text-sm font-medium" style={{ color: GREEN }}>
          The Industry's Leading Affiliate Program
        </span>
      </div>
      
      {/* Main Headline */}
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-6">
        <span className="text-foreground">Zero Caps. Zero Limits.</span>
        <br />
        <span 
          className="bg-clip-text text-transparent animate-gradient-shift"
          style={{
            backgroundImage: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_LIGHT} 50%, ${GREEN} 100%)`,
            backgroundSize: "200% 200%",
          }}
        >
          Forever.
        </span>
      </h1>
      
      {/* Subheadline */}
      <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
        The industry's most generous affiliate program. Lifetime commissions on every referral. 
        No limits on what you can earn. Build a sustainable business for life.
      </p>
      
      {/* Quick stats in cards */}
      <div className="flex flex-wrap justify-center gap-4 md:gap-6 mt-8">
        <div 
          className="min-w-[140px] px-6 py-4 rounded-xl text-center transition-transform hover:scale-105"
          style={{ 
            background: `linear-gradient(135deg, ${GREEN}10 0%, transparent 70%)`,
            border: `1px solid ${GREEN}25`,
            boxShadow: `0 4px 24px ${GREEN}10`,
          }}
        >
          <div className="text-3xl md:text-4xl font-bold" style={{ color: GREEN }}>50%</div>
          <div className="text-sm text-muted-foreground mt-1">Max Commission</div>
        </div>
        <div 
          className="min-w-[140px] px-6 py-4 rounded-xl text-center transition-transform hover:scale-105"
          style={{ 
            background: `linear-gradient(135deg, ${GREEN}10 0%, transparent 70%)`,
            border: `1px solid ${GREEN}25`,
            boxShadow: `0 4px 24px ${GREEN}10`,
          }}
        >
          <div className="text-3xl md:text-4xl font-bold" style={{ color: GREEN }}>∞</div>
          <div className="text-sm text-muted-foreground mt-1">Unlimited Earnings</div>
        </div>
        <div 
          className="min-w-[140px] px-6 py-4 rounded-xl text-center transition-transform hover:scale-105"
          style={{ 
            background: `linear-gradient(135deg, ${GREEN}10 0%, transparent 70%)`,
            border: `1px solid ${GREEN}25`,
            boxShadow: `0 4px 24px ${GREEN}10`,
          }}
        >
          <div className="text-3xl md:text-4xl font-bold" style={{ color: GREEN }}>Lifetime</div>
          <div className="text-sm text-muted-foreground mt-1">Referral Duration</div>
        </div>
      </div>
    </div>
  </section>
);

// Comparison Block Component
const ComparisonBlock = () => {
  const comparisons = [
    { others: "90-day cookie expiry", nordex: "Lifetime attribution" },
    { others: "Capped earnings per referral", nordex: "Unlimited per referral" },
    { others: "Monthly earning limits", nordex: "No monthly caps" },
    { others: "Time-limited commissions", nordex: "Earn forever" },
  ];

  return (
    <section className="py-14 md:py-20 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
          Why Affiliates Choose <span style={{ color: GREEN }}>Nordex</span>
        </h2>
        <p className="text-muted-foreground text-center mb-12 max-w-xl mx-auto text-base">
          Other programs cap your potential. We don't believe in limits.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {/* Others Column */}
          <div className="rounded-2xl bg-card/50 border border-border/50 p-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="size-10 rounded-full bg-rose-500/10 flex items-center justify-center">
                <XCircleIcon className="size-5 text-rose-500" />
              </div>
              <h3 className="text-lg font-semibold text-muted-foreground">Other Programs</h3>
            </div>
            <div className="space-y-5">
              {comparisons.map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <XCircleIcon className="size-6 text-rose-500/70 shrink-0" />
                  <span className="text-base text-muted-foreground">{item.others}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Nordex Column */}
          <div 
            className="rounded-2xl p-8 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${GREEN}12 0%, transparent 50%)`,
              border: `1px solid ${GREEN}35`,
              boxShadow: `0 8px 32px ${GREEN}10`,
            }}
          >
            {/* Subtle glow accent */}
            <div 
              className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-[60px] opacity-40"
              style={{ background: GREEN }}
            />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="size-10 rounded-full flex items-center justify-center" style={{ background: `${GREEN}20` }}>
                  <CheckCircle2Icon className="size-5" style={{ color: GREEN }} />
                </div>
                <h3 className="text-lg font-semibold" style={{ color: GREEN }}>Nordex</h3>
              </div>
              <div className="space-y-5">
                {comparisons.map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <CheckCircle2Icon className="size-6 shrink-0" style={{ color: GREEN }} />
                    <span className="text-base text-foreground font-medium">{item.nordex}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Commission Tiers Component
const CommissionTiers = () => {
  const sharedFeatures = [
    "No caps on earnings",
    "Earn forever",
    "Unlimited per referral",
    "Lifetime attribution",
  ];

  const tiers = [
    {
      name: "Partner",
      tier: "Tier 1",
      commission: "45%",
      description: "Start earning immediately",
      features: sharedFeatures,
      highlight: false,
    },
    {
      name: "Partner Pro",
      tier: "Tier 2",
      commission: "50%",
      description: "Unlock higher earnings",
      features: sharedFeatures,
      highlight: false,
    },
    {
      name: "Partner Elite",
      tier: "Tier 3",
      commission: "50%",
      description: "Maximum earning potential",
      features: sharedFeatures,
      highlight: true,
      hasVault: true,
    },
  ];

  return (
    <section className="py-12 md:py-16 px-4 relative overflow-hidden">
      {/* Background glow for tiers section */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full opacity-10 blur-[150px]"
        style={{ background: GREEN }}
      />
      
      <div className="relative z-10 max-w-5xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
          Start Where <span style={{ color: GREEN }}>Others Peak</span>
        </h2>
        <p className="text-muted-foreground text-center mb-10 max-w-xl mx-auto">
          Other programs' VIP tiers can't match our entry level. And it only gets better.
        </p>
        
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {tiers.map((tier) => (
            <div 
              key={tier.name}
              className="relative rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02] bg-card border-2"
              style={{ borderColor: `${GREEN}30` }}
            >
              {/* Green glow effect for all tiers */}
              <div 
                className="absolute -inset-[1px] rounded-2xl opacity-40 blur-sm -z-10"
                style={{ background: `linear-gradient(135deg, ${GREEN}30, transparent)` }}
              />
              
              {/* EXTREME EARNINGS badge - only for Elite tier */}
              {tier.highlight && (
                <div className="absolute top-0 right-0 px-3 py-1 text-xs font-bold rounded-bl-xl rounded-tr-xl" 
                  style={{ background: GREEN, color: '#000' }}>
                  EXTREME EARNINGS
                </div>
              )}
              
              {/* Tier badge */}
              <div className="text-xs font-medium text-muted-foreground mb-2">{tier.tier}</div>
              
              {/* Tier name */}
              <h3 className="text-xl font-bold mb-1">{tier.name}</h3>
              
              {/* Commission rate */}
              <div className="flex items-baseline gap-1 mb-2">
                <span 
                  className="text-4xl font-bold"
                  style={{ color: GREEN }}
                >
                  {tier.commission}
                </span>
                <span className="text-sm text-muted-foreground">on trading fees</span>
              </div>
              
              {/* Description */}
              <p className="text-sm text-muted-foreground mb-4">{tier.description}</p>
              
              {/* Features */}
              <ul className="space-y-2">
                {tier.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <ChevronRightIcon className="size-4" style={{ color: GREEN }} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              {/* Performance Vault teaser for Elite tier */}
              {tier.hasVault && (
                <div 
                  className="mt-4 p-3 rounded-lg border border-dashed flex items-center gap-2"
                  style={{ borderColor: `${GREEN}40`, background: `${GREEN}05` }}
                >
                  <div className="size-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <LockIcon className="size-4 text-amber-500" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-amber-500">Performance Vault</div>
                    <div className="text-[10px] text-muted-foreground">Exclusive access for top performers</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Dashboard Preview Component
const DashboardPreview = () => {
  const features = [
    { icon: LinkIcon, title: "Create unlimited custom links", description: "Generate branded referral URLs" },
    { icon: TrendingUpIcon, title: "Adjust referral discounts", description: "Set custom discounts in real-time" },
    { icon: BarChart3Icon, title: "Track everything", description: "Clicks, conversions, signups, volume" },
    { icon: WalletIcon, title: "Monitor your revenue", description: "Real-time earnings dashboard" },
    { icon: ZapIcon, title: "Request instant payouts", description: "Withdraw anytime to your wallet" },
  ];

  return (
    <section className="py-12 md:py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
          Your <span style={{ color: GREEN }}>Command Center</span>
        </h2>
        <p className="text-muted-foreground text-center mb-10 max-w-xl mx-auto">
          A powerful dashboard to track, optimize, and grow your affiliate business.
        </p>
        
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Browser Mockup Frame (Ready for screenshot) */}
          <div className="order-2 lg:order-1">
            <div className="rounded-xl overflow-hidden border border-border/50 bg-card shadow-2xl">
              {/* Browser chrome */}
              <div className="bg-muted/50 px-4 py-3 flex items-center gap-2 border-b border-border/50">
                <div className="flex gap-1.5">
                  <div className="size-3 rounded-full bg-rose-500/70" />
                  <div className="size-3 rounded-full bg-amber-500/70" />
                  <div className="size-3 rounded-full bg-emerald-500/70" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-4 py-1 rounded-md bg-background/50 text-xs text-muted-foreground">
                    nordex.trade/affiliate
                  </div>
                </div>
              </div>
              {/* Dashboard placeholder */}
              <div className="aspect-[16/10] bg-gradient-to-br from-card via-card to-muted/20 flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="size-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${GREEN}15` }}>
                    <BarChart3Icon className="size-8" style={{ color: GREEN }} />
                  </div>
                  <p className="text-sm text-muted-foreground">Dashboard Preview</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Coming soon</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Features list */}
          <div className="order-1 lg:order-2 space-y-4">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="flex items-start gap-4 p-4 rounded-xl bg-card/50 border border-border/30 hover:border-border/50 transition-colors"
              >
                <div 
                  className="size-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${GREEN}15` }}
                >
                  <feature.icon className="size-5" style={{ color: GREEN }} />
                </div>
                <div>
                  <h4 className="font-semibold text-sm">{feature.title}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Custom input styling - brighter border to match textarea, thin white focus border
const inputClassName = "bg-background/50 border border-white/20 focus:border-white/70 focus:outline-none !ring-0 !ring-offset-0 transition-colors";

// Deposit-style glow button (transparent with rotating gradient border)
const GlowButton = ({ 
  children, 
  isPending, 
  type = "button" 
}: { 
  children: React.ReactNode; 
  isPending: boolean; 
  type?: "button" | "submit" 
}) => (
  <button
    type={type}
    disabled={isPending}
    className="relative w-full h-14 rounded-xl font-semibold text-base overflow-visible transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 group cursor-pointer"
    style={{ color: GREEN }}
  >
    {/* Center glow effect on hover - behind button */}
    <span 
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-16 rounded-full opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 -z-10"
      style={{ background: `radial-gradient(ellipse, ${GREEN}60 0%, ${GREEN}30 40%, transparent 70%)` }}
    />
    
    {/* Animated rotating gradient border - the traveling light */}
    <span className="absolute inset-0 rounded-xl overflow-hidden p-[1.5px]">
      <span 
        className="absolute inset-[-150%] transition-opacity duration-300"
        style={{
          background: `conic-gradient(from 0deg, transparent 60%, ${GREEN}99 75%, ${GREEN_LIGHT}E6 85%, ${GREEN}99 95%, transparent 100%)`,
          animation: 'border-spin 3s linear infinite',
          opacity: 0.7,
        }}
      />
    </span>
    
    {/* Static border */}
    <span 
      className="absolute inset-0 rounded-xl"
      style={{ border: `1px solid ${GREEN}4D` }}
    />
    
    {/* Button background - glass effect (transparent) */}
    <span className="absolute inset-[1.5px] rounded-[10px] bg-background/95 backdrop-blur-sm" />
    
    {/* Content */}
    <span className="relative z-10 flex items-center justify-center gap-2">
      {children}
    </span>
  </button>
);

// Social link entry type
type SocialEntry = {
  url: string;
  audience: string;
};

// Application Form Component
const ApplicationForm = ({ 
  socialEntries, setSocialEntries,
  name, setName,
  email, setEmail,
  note, setNote,
  onSubmit,
  isPending
}: {
  socialEntries: SocialEntry[];
  setSocialEntries: (v: SocialEntry[]) => void;
  name: string;
  setName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
}) => {
  const addSocialEntry = () => {
    if (socialEntries.length < 6) {
      setSocialEntries([...socialEntries, { url: "", audience: "" }]);
    }
  };

  const removeSocialEntry = (index: number) => {
    if (socialEntries.length > 1) {
      setSocialEntries(socialEntries.filter((_, i) => i !== index));
    }
  };

  const updateSocialEntry = (index: number, field: keyof SocialEntry, value: string) => {
    const updated = [...socialEntries];
    updated[index] = { ...updated[index], [field]: value };
    setSocialEntries(updated);
  };

  return (
    <section className="py-12 md:py-16 px-4" id="apply">
      <div className="max-w-2xl mx-auto">
        <div 
          className="rounded-2xl p-6 md:p-8 relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${GREEN}08 0%, transparent 50%)`,
            border: `1px solid ${GREEN}20`,
          }}
        >
          {/* Glow effect */}
          <div 
            className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-[80px] opacity-30"
            style={{ background: GREEN }}
          />
          
          <div className="relative z-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">
                Start Earning <span style={{ color: GREEN }}>Today</span>
              </h2>
              <p className="text-sm text-muted-foreground">
                Tell us a bit about yourself to help us review your application.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              {/* Name field */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Name</label>
                <Input
                  placeholder="Your name or online alias"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClassName}
                />
              </div>

              {/* Contact Email field */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Contact Email</label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClassName}
                />
              </div>

              {/* Social Media / Community Links - Dynamic with audience size */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Social Media or Community</label>
                  {socialEntries.length < 6 && (
                    <button
                      type="button"
                      onClick={addSocialEntry}
                      className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-colors hover:bg-white/10"
                      style={{ color: GREEN }}
                    >
                      <PlusIcon className="size-3.5" />
                      Add another
                    </button>
                  )}
                </div>
                
                {socialEntries.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {/* URL input - 2/3 width */}
                    <div className="flex-[2]">
                      <Input
                        placeholder={index === 0 ? "https://instagram.com/yourhandle" : "Social media or community URL"}
                        value={entry.url}
                        onChange={(e) => updateSocialEntry(index, "url", e.target.value)}
                        className={inputClassName}
                      />
                    </div>
                    {/* Audience size input - 1/3 width */}
                    <div className="flex-1">
                      <Input
                        placeholder="e.g., 100k"
                        value={entry.audience}
                        onChange={(e) => updateSocialEntry(index, "audience", e.target.value)}
                        className={inputClassName}
                      />
                    </div>
                    {/* Remove button */}
                    {socialEntries.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSocialEntry(index)}
                        className="p-2 rounded-lg text-muted-foreground hover:text-rose-400 hover:bg-rose-500/10 transition-colors shrink-0"
                      >
                        <TrashIcon className="size-4" />
                      </button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  Instagram, X, YouTube, TikTok, Telegram, Discord, or your website
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Additional Notes</label>
                <textarea
                  placeholder="Tell us why you want to be an affiliate and how you plan to promote Nordex..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className={`w-full min-h-[100px] px-3 py-2 text-sm rounded-lg placeholder:text-muted-foreground resize-none ${inputClassName}`}
                />
              </div>

              {/* Terms */}
              <div className="bg-muted/20 rounded-lg p-4 text-xs text-muted-foreground">
                <p className="flex items-start gap-2">
                  <CheckCircle2Icon className="size-4 shrink-0 mt-0.5" style={{ color: GREEN }} />
                  By applying, you agree to our affiliate program terms. We review applications
                  within 24-48 hours and will notify you via email.
                </p>
              </div>

              <GlowButton type="submit" isPending={isPending}>
                {isPending ? (
                  <>
                    <Loader2Icon className="size-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <ArrowRightIcon className="size-4" />
                  </>
                )}
              </GlowButton>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section Component
const FAQSection = () => {
  const faqs = [
    {
      question: "How much can I really earn?",
      answer: "There's no ceiling. You earn up to 50% commission on every trade your referrals make. Top affiliates earn five figures monthly. With no caps on per-referral earnings or total earnings, your income scales with your reach."
    },
    {
      question: "How long do I earn from each referral?",
      answer: "Forever. Literally. Once a user signs up with your link, they're attributed to you for life. Every trade they make, you earn. There's no 90-day cookie, no annual reset, no expiration."
    },
    {
      question: "What makes Nordex different from other programs?",
      answer: "Most programs cap your earnings, limit referral duration, or take a bigger cut. We don't. We offer the highest commissions in the industry, lifetime attribution, instant payouts, and a dashboard that actually helps you grow."
    },
    {
      question: "How do payouts work?",
      answer: "Request payouts anytime directly to your Solana wallet. Minimum payout is $100. Most payouts are processed within 24 hours. No waiting, no hoops to jump through."
    },
    {
      question: "What is the Performance Vault?",
      answer: "An exclusive program for our top-tier Elite affiliates. We can't reveal all the details yet, but it includes special bonuses, early access to features, and rewards that go beyond standard commissions. Reach Elite tier to unlock access."
    },
  ];

  return (
    <section className="py-12 md:py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
          Frequently Asked <span style={{ color: GREEN }}>Questions</span>
        </h2>
        
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div 
              key={i} 
              className="rounded-xl bg-card/50 border border-border/50 p-5 hover:border-border transition-colors"
            >
              <h3 className="font-semibold mb-2 flex items-start gap-2">
                <ChevronRightIcon className="size-5 shrink-0 mt-0.5" style={{ color: GREEN }} />
                {faq.question}
              </h3>
              <p className="text-sm text-muted-foreground pl-7">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main Component
export const AffiliateApplicationForm = () => {
  const [socialEntries, setSocialEntries] = useState<SocialEntry[]>([{ url: "", audience: "" }]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  const applyMutation = useAffiliateApplication();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Combine all non-empty social entries into formatted strings
    const validEntries = socialEntries.filter(entry => entry.url.trim());
    const socialMediaCombined = validEntries
      .map(entry => entry.audience.trim() ? `${entry.url} (${entry.audience})` : entry.url)
      .join(", ");
    const audienceCombined = validEntries
      .filter(entry => entry.audience.trim())
      .map(entry => entry.audience)
      .join(", ");

    // Include name and email in the note if provided
    let fullNote = "";
    if (name.trim()) fullNote += `Name: ${name.trim()}\n`;
    if (email.trim()) fullNote += `Contact Email: ${email.trim()}\n`;
    if (note.trim()) fullNote += note.trim();

    try {
      await applyMutation.mutateAsync({
        socialMedia: socialMediaCombined || undefined,
        website: undefined, // Removed - now combined with social media
        audience: audienceCombined || undefined,
        note: fullNote.trim() || undefined,
      });
      toast.success("Application submitted! We'll review it shortly.");
    } catch (_error) {
      toast.error("Failed to submit application. Please try again.");
    }
  };

  return (
    <main className="flex-1 overflow-auto bg-background">
      {/* Hero Section */}
      <HeroSection />
      
      {/* Comparison Block */}
      <ComparisonBlock />
      
      {/* Commission Tiers */}
      <CommissionTiers />
      
      {/* Dashboard Preview */}
      <DashboardPreview />
      
      {/* Application Form */}
      <ApplicationForm
        socialEntries={socialEntries}
        setSocialEntries={setSocialEntries}
        name={name}
        setName={setName}
        email={email}
        setEmail={setEmail}
        note={note}
        setNote={setNote}
        onSubmit={handleSubmit}
        isPending={applyMutation.isPending}
      />
      
      {/* FAQ Section */}
      <FAQSection />
      
      {/* Bottom CTA */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-muted-foreground mb-4">
            Ready to build a sustainable income stream?
          </p>
          <a 
            href="#apply" 
            className="inline-flex items-center gap-2 font-semibold transition-colors hover:opacity-80"
            style={{ color: GREEN }}
          >
            Apply Now
            <ArrowRightIcon className="size-4" />
          </a>
        </div>
      </section>
    </main>
  );
};
